/**
 * The four ways somebody gets into the portal, and the one thing they do once they are in.
 *
 * **The link is not replaced by the password. It is what the password is built on.** A tenant looks
 * at this twice a year and a link is the right answer for them; a buyer three years into an
 * instalment plan looks every month and should not hunt for one. So both are on the screen at once
 * — an address and a password to sign in, and an address on its own to be sent a link. The link is
 * also how a password is *set*, and the only way back for somebody who has forgotten one: there is
 * no second reset path, which is exactly why it can never be taken away. ADR-0102.
 *
 * **Nothing here says who exists.** Asking for a link and registering both answer the same way for
 * an address that is on an account and one that is not, because the endpoints do — otherwise these
 * forms become a way to test whether a named person rents from, or is buying from, a named agency.
 * The screens must not undo that by being helpful.
 */

import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Alert, Button, Card, Field, Input, Spinner } from "@/components/ui";
import { PasswordInput } from "@/components/password-input";
import { IconMail } from "@/components/icons";
import { usePageTitle } from "@/lib/title";
import { PortalProvider, usePortal } from "@/lib/portal";
import { PortalShell } from "./PortalShell";

/** One of the agencies an address is on the books of. Returned by `sign-in` when there is more than one. */
interface Choice {
  partyId: string;
  orgId: string;
  orgName: string;
  name: string;
}

function messageOf(problem: unknown, fallback: string): string {
  return problem instanceof Error ? problem.message : fallback;
}

/* ---------------------------------------------------------------- getting in */

/**
 * Signing in: with a password, or by asking for a link.
 *
 * One form, two buttons, and that is deliberate. A tab strip or a "sign in another way" link would
 * hide whichever half a given reader needs behind a control they have to understand first — and the
 * two readers here are exactly the people least likely to work it out: somebody who has never set a
 * password, and somebody who has forgotten one. Both routes are visible without a decision.
 *
 * The password field is not `required` on the element. Asking for a link needs the address alone,
 * and a browser will not let a form submit past a required field the reader was never meant to
 * fill in.
 */
export function PortalSignIn() {
  usePageTitle("Your account");
  const { post, accept } = usePortal();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState<null | "password" | "link">(null);
  const [error, setError] = useState<string | null>(null);
  /** Set when this address is on more than one agency's books. Nothing is issued until they pick. */
  const [choices, setChoices] = useState<Choice[] | null>(null);

  const signIn = async (partyId?: string) => {
    setBusy("password");
    setError(null);
    try {
      const result = await post<{ ok: boolean; token?: string; choose?: Choice[] }>(
        "/sign-in",
        { email, password, ...(partyId ? { partyId } : {}) },
        "sign_in",
      );
      /*
        `{ ok: false, choose: [...] }` is an ordinary answer, not a failure.

        Somebody who rents from one agency and is buying from another has two sets of books on one
        address, and a password — unlike a link, which is sent once per agency — cannot say which
        they meant. A client that reads only `token` looks broken for exactly those people.

        The `partyId` sent back is a convenience and is written as one: the server checks it against
        the identities the account actually resolves to before it issues anything, so nothing here is
        holding a gate shut.
      */
      if (!result.ok && result.choose?.length) {
        setChoices(result.choose);
        return;
      }
      if (result.token) {
        accept(result.token);
        navigate("/portal", { replace: true });
      }
    } catch (problem) {
      setError(messageOf(problem, "Those details could not be checked. Try again shortly."));
      setChoices(null);
    } finally {
      setBusy(null);
    }
  };

  const requestLink = async () => {
    if (!email.trim()) {
      setError("Enter the email address your agency holds for you.");
      return;
    }
    setBusy("link");
    setError(null);
    try {
      await post("/request-link", { email }, "portal_link");
      setSent(true);
    } catch (problem) {
      setError(messageOf(problem, "Something went wrong. Try again shortly."));
    } finally {
      setBusy(null);
    }
  };

  if (sent) {
    return (
      <PortalShell>
        <Card>
          <h1 className="text-xl font-semibold">Check your inbox</h1>
          {/*
            The same message whatever happened, because the endpoint gives the same answer. Saying
            "we found you" would turn this form into a way of testing whether a named person rents
            from a named agency, which is the fact a tenant is most entitled to keep.
          */}
          <div className="mt-4">
            <Alert tone="success" title="A link is on its way">
              If that address is on an account, a link is on its way. It works for one hour and can be used once. If
              nothing arrives, check the address with your agency — they may hold a different one for you.
            </Alert>
          </div>
          <p className="mt-4 text-sm text-ink-500">
            The link signs you in, and once you are in you can set a password so the next time is quicker.
          </p>
          <button
            type="button"
            className="link mt-4 text-sm"
            onClick={() => {
              setSent(false);
              setError(null);
            }}
          >
            Back
          </button>
        </Card>
      </PortalShell>
    );
  }

  if (choices) {
    return (
      <PortalShell>
        <Card>
          <h1 className="text-xl font-semibold">Which account?</h1>
          <p className="mt-1 text-sm text-ink-500">
            This address is on the books of more than one agency. Open one now — you can come back and sign in to the
            other whenever you like.
          </p>
          <ul className="mt-5 space-y-2">
            {choices.map((choice) => (
              <li key={choice.partyId}>
                <button
                  type="button"
                  disabled={busy !== null}
                  onClick={() => void signIn(choice.partyId)}
                  className="w-full rounded-[var(--radius-surface)] border border-ink-200 bg-white px-4 py-3 text-left hover:border-brand-400 hover:bg-brand-50"
                >
                  <span className="block font-semibold text-ink-900">{choice.orgName}</span>
                  <span className="block text-sm text-ink-500">{choice.name}</span>
                </button>
              </li>
            ))}
          </ul>
          {error ? (
            <div className="mt-4">
              <Alert tone="danger">{error}</Alert>
            </div>
          ) : null}
          <button type="button" className="link mt-4 text-sm" onClick={() => setChoices(null)}>
            Use a different address
          </button>
        </Card>
      </PortalShell>
    );
  }

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!password) {
      setError("Enter your password, or ask for a link instead.");
      return;
    }
    void signIn();
  };

  return (
    <PortalShell>
      <Card>
        <h1 className="text-xl font-semibold">Your account</h1>
        <p className="mt-1 text-sm text-ink-500">
          See what you are renting or buying, what is due, and every receipt. Use the email address your agency holds
          for you.
        </p>

        <form onSubmit={submit} className="mt-5 space-y-4">
          {error ? <Alert tone="danger">{error}</Alert> : null}

          <Field label="Email address" htmlFor="email" required>
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="username"
              required
              placeholder="you@example.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>

          <Field label="Password" htmlFor="password" hint="Leave this empty if you would rather be sent a link.">
            <PasswordInput
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>

          <Button type="submit" disabled={busy !== null} className="w-full">
            {busy === "password" ? "Signing in…" : "Sign in"}
          </Button>

          {/* The link, kept as a control of its own rather than as small print. It is the way in for
              anybody who has never set a password and the only way back for anybody who has
              forgotten one, so it cannot be the thing somebody has to go looking for. */}
          <div className="flex items-center gap-3 text-xs uppercase tracking-wider text-ink-400">
            <span className="h-px flex-1 bg-ink-200" />
            or
            <span className="h-px flex-1 bg-ink-200" />
          </div>

          <Button
            type="button"
            variant="secondary"
            data-portal-link
            disabled={busy !== null}
            onClick={() => void requestLink()}
            className="w-full"
          >
            {busy === "link" ? "Sending…" : "Email me a link instead"}
          </Button>

          <p className="flex items-start gap-2 text-xs text-ink-500">
            <IconMail className="mt-0.5 size-4 shrink-0" />
            <span>
              No password yet, or forgotten it? The link is the way in either way — it signs you in without one, and
              you can set a password afterwards.
            </span>
          </p>
        </form>

        <p className="mt-5 border-t border-ink-100 pt-4 text-sm text-ink-500">
          Bought from an agency that has never sent you anything?{" "}
          <Link to="/portal/sign-up" className="link">
            Register
          </Link>
        </p>
      </Card>
    </PortalShell>
  );
}

/* ------------------------------------------------------------- following the link */

export function EnterWithLink() {
  usePageTitle("Signing you in");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { enter } = usePortal();
  const token = params.get("token") ?? "";
  const [error, setError] = useState<string | null>(null);
  /*
    React runs effects twice in development. Redeeming is single use — deliberately — so the second
    run reports "already used" on a link that had in fact just worked, and the reader is told to ask
    for another one they do not need. A ref rather than state because it must not itself cause a
    render, and it must be set before the first request goes out.
  */
  const attempted = useRef(false);

  useEffect(() => {
    if (!token) {
      setError("That link is incomplete. Open it from the email exactly as it was sent.");
      return;
    }
    if (attempted.current) return;
    attempted.current = true;

    /*
      No cancelled flag, and its absence is the fix rather than an oversight.

      There was one, and together with the guard above it meant nobody ever got in. React runs an
      effect, then its cleanup, then the effect again in development. The first run redeemed the
      link; the first cleanup set cancelled; the second run returned early on the guard; and when
      the first request came back it declined to navigate because cancelled was true. The link
      worked, the session was stored, and the page sat on "Signing you in" for ever — which is
      exactly what the suite kept reporting and no assertion could explain.

      The guard alone does the whole job. Redemption happens once, so its result is always the one
      worth acting on, and acting on it is the only way anybody arrives.
    */
    enter(token)
      .then(() => navigate("/portal", { replace: true }))
      .catch((problem: unknown) => {
        setError(problem instanceof Error ? problem.message : "That link could not be used.");
      });
    // The token comes from the address and `enter` is stable; re-running would redeem a single-use
    // link twice and report failure on one that had just worked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <PortalShell>
      <Card>
        {error ? (
          <>
            <h1 className="text-xl font-semibold">That link did not work</h1>
            <div className="mt-3">
              <Alert tone="danger">{error}</Alert>
            </div>
            <p className="mt-3 text-sm text-ink-500">
              A link can be used once and lasts an hour. Ask for a fresh one and it will work.
            </p>
            <Link to="/portal/sign-in" className="btn btn-primary mt-5 w-full">
              Ask for a new link
            </Link>
          </>
        ) : (
          <div className="flex items-center gap-3">
            <Spinner />
            <p className="text-sm text-ink-500">Signing you in…</p>
          </div>
        )}
      </Card>
    </PortalShell>
  );
}

/* ------------------------------------------------------------------ registering */

/**
 * Registering without having been invited.
 *
 * Somebody who bought a plot from an agency that never sent them anything can start here. Nothing
 * is opened by registering: the account matches records only once the address is confirmed, and
 * then only against a client record carrying that address — so registering with somebody else's
 * address reaches nothing at all.
 *
 * The answer is the same for an address that already has an account and one that does not, because
 * the endpoint gives the same answer to both. Anything else here would be a way to find out who
 * has registered.
 */
export function PortalSignUp() {
  usePageTitle("Register");
  const { post } = usePortal();
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await post(
        "/sign-up",
        {
          name: String(form.get("name") ?? ""),
          email: String(form.get("email") ?? ""),
          password: String(form.get("password") ?? ""),
        },
        "sign_up",
      );
      try {
        window.localStorage.setItem(REGISTERED_KEY, "1");
      } catch {
        /* Private mode throws. The confirmation page then offers both doors, which is the safe
           answer anyway. */
      }
      setDone(true);
    } catch (problem) {
      setError(messageOf(problem, "That could not be sent. Try again shortly."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PortalShell>
      <Card>
        {done ? (
          <>
            <h1 className="text-xl font-semibold">Check your inbox</h1>
            <div className="mt-4">
              <Alert tone="success" title="Confirm your address">
                A message is on its way. Open the link in it to confirm the address — that is what matches you to your
                records.
              </Alert>
            </div>
            <p className="mt-4 text-sm text-ink-500">
              Once it is confirmed you can sign in. If nothing appears afterwards, the agency may hold a different
              address for you — ask them to link this one, which takes them a moment.
            </p>
            <Link to="/portal/sign-in" className="btn btn-secondary mt-5 w-full">
              Go to sign in
            </Link>
          </>
        ) : (
          <>
            <h1 className="text-xl font-semibold">Register</h1>
            <p className="mt-1 text-sm text-ink-500">
              For anybody renting or buying who has not been sent a link. Use the address the agency has for you — it is
              what matches you to your records.
            </p>

            <form onSubmit={submit} className="mt-5 space-y-4">
              {error ? <Alert tone="danger">{error}</Alert> : null}

              <Field label="Your name" htmlFor="name" required>
                <Input id="name" name="name" required minLength={2} autoComplete="name" placeholder="Chidi Okafor" />
              </Field>

              <Field label="Email address" htmlFor="email" required>
                <Input id="email" name="email" type="email" required autoComplete="username" placeholder="you@example.com" />
              </Field>

              <Field label="Choose a password" htmlFor="password" required hint="At least 8 characters.">
                <PasswordInput id="password" required minLength={8} autoComplete="new-password" />
              </Field>

              <Button type="submit" disabled={busy} className="w-full">
                {busy ? "Registering…" : "Register"}
              </Button>
            </form>

            <p className="mt-5 text-sm text-ink-500">
              Already have an account?{" "}
              <Link to="/portal/sign-in" className="link">
                Sign in
              </Link>
            </p>
          </>
        )}
      </Card>
    </PortalShell>
  );
}

/* ------------------------------------------------------------ setting a password */

/** Remembered per browser so the offer on the account screen stops once it has been taken up. */
export const PASSWORD_SET_KEY = "rentorng.portal.password";

/**
 * Remembered per browser so the confirmation page knows which door this person came from.
 *
 * The confirmation link a registrant receives is built by the API and points at `/verify-email`,
 * which belongs to the agency application — the same link and the same table serve both kinds of
 * account, which is the reuse ADR-0102 asked for. That page cannot tell the two apart from the
 * token, so this is the breadcrumb: set here, read there, and treated as a hint about which door to
 * put first rather than as a fact about who anybody is. Mail read on another device simply gets
 * both doors offered.
 */
export const REGISTERED_KEY = "rentorng.portal.registered";

export function rememberPasswordSet(): void {
  try {
    window.localStorage.setItem(PASSWORD_SET_KEY, "1");
  } catch {
    /* Private mode throws. The offer simply appears again, which costs nobody anything. */
  }
}

/**
 * Setting a password, from a session that already exists.
 *
 * Reached from the offer on the account screen, and only ever from a live session — the endpoint
 * needs one, and the session is the proof: whoever holds it read a link sent to the address the
 * agency holds. That is why the current password is not asked for, and why it must not be: somebody
 * who has forgotten theirs would be holding a session they could not use to fix anything.
 */
export function PortalSetPassword() {
  usePageTitle("Set a password");
  const { loading, me, post } = usePortal();
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const password = String(form.get("password") ?? "");
    if (password !== String(form.get("confirm") ?? "")) {
      setError("Those two do not match.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await post("/set-password", { password });
      rememberPasswordSet();
      setDone(true);
    } catch (problem) {
      setError(messageOf(problem, "That could not be saved. Try again shortly."));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <PortalShell>
        <div className="flex items-center gap-3">
          <Spinner />
          <p className="text-sm text-ink-500">Loading…</p>
        </div>
      </PortalShell>
    );
  }

  if (!me) {
    return (
      <PortalShell>
        <Card>
          <h1 className="text-xl font-semibold">Sign in first</h1>
          <p className="mt-1 text-sm text-ink-500">
            A password is set from inside your account. Ask for a link, follow it, and the offer is waiting there.
          </p>
          <Link to="/portal/sign-in" className="btn btn-primary mt-5 w-full">
            Ask for a link
          </Link>
        </Card>
      </PortalShell>
    );
  }

  return (
    <PortalShell agency={me.agency}>
      <Card>
        {done ? (
          <>
            <h1 className="text-xl font-semibold">Password set</h1>
            <p className="mt-1 text-sm text-ink-500">
              Next time, sign in with your email address and that password. Links still work — ask for one whenever you
              forget it.
            </p>
            <Link to="/portal" className="btn btn-primary mt-5 w-full">
              Back to your account
            </Link>
          </>
        ) : (
          <>
            <h1 className="text-xl font-semibold">Set a password</h1>
            <p className="mt-1 text-sm text-ink-500">
              So you can sign in whenever you like without waiting for a link. If you already have one, this replaces
              it.
            </p>

            <form onSubmit={submit} className="mt-5 space-y-4">
              {error ? <Alert tone="danger">{error}</Alert> : null}

              <Field label="New password" htmlFor="password" required hint="At least 8 characters.">
                <PasswordInput id="password" required minLength={8} autoComplete="new-password" />
              </Field>

              <Field label="Type it again" htmlFor="confirm" required>
                <PasswordInput id="confirm" name="confirm" required minLength={8} autoComplete="new-password" />
              </Field>

              <Button type="submit" disabled={busy} className="w-full">
                {busy ? "Saving…" : "Save password"}
              </Button>

              <Link to="/portal" className="link block text-center text-sm">
                Not now
              </Link>
            </form>
          </>
        )}
      </Card>
    </PortalShell>
  );
}

/* --------------------------------------------------------------------- the routes */

export function PortalSignInPage() {
  return (
    <PortalProvider>
      <PortalSignIn />
    </PortalProvider>
  );
}

export function PortalEnterPage() {
  return (
    <PortalProvider>
      <EnterWithLink />
    </PortalProvider>
  );
}

export function PortalSignUpPage() {
  return (
    <PortalProvider>
      <PortalSignUp />
    </PortalProvider>
  );
}

export function PortalSetPasswordPage() {
  return (
    <PortalProvider>
      <PortalSetPassword />
    </PortalProvider>
  );
}
